// @ts-nocheck
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Plus, Trash2, Clock, Sparkles, CalendarClock, Send, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow, isPast, isToday, isTomorrow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Reminder {
  id: string;
  title: string;
  message: string | null;
  remind_at: string;
  icon: string;
  priority: string;
  sent: boolean;
  created_at: string;
}

const ICONS = [
  { value: "🔔", label: "Sino" },
  { value: "📋", label: "Tarefa" },
  { value: "⚖️", label: "Jurídico" },
  { value: "📞", label: "Ligação" },
  { value: "📧", label: "Email" },
  { value: "🏛️", label: "Tribunal" },
  { value: "💰", label: "Pagamento" },
  { value: "📅", label: "Agenda" },
  { value: "⏰", label: "Urgente" },
  { value: "🎂", label: "Aniversário" },
  { value: "✈️", label: "Viagem" },
  { value: "💊", label: "Saúde" },
];

const PRIORITIES = [
  { value: "low", label: "Baixa", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  { value: "normal", label: "Normal", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  { value: "high", label: "Alta", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  { value: "urgent", label: "Urgente", color: "bg-red-500/10 text-red-600 border-red-500/20" },
];

export const CustomReminders = () => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const [form, setForm] = useState({
    title: "",
    message: "",
    date: "",
    time: "",
    icon: "🔔",
    priority: "normal",
  });

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("custom_reminders")
        .select("*")
        .eq("user_id", user.id)
        .order("remind_at", { ascending: true });

      if (error) throw error;
      setReminders(data || []);
    } catch (err: any) {
      console.error("Erro ao buscar lembretes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.date || !form.time) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Não autenticado");

      const remindAt = new Date(`${form.date}T${form.time}:00`);
      if (isPast(remindAt)) {
        toast({ title: "A data/hora deve ser no futuro", variant: "destructive" });
        setSaving(false);
        return;
      }

      const { error } = await supabase.from("custom_reminders").insert({
        user_id: user.id,
        title: form.title,
        message: form.message || null,
        remind_at: remindAt.toISOString(),
        icon: form.icon,
        priority: form.priority,
      });

      if (error) throw error;

      toast({ title: "✅ Lembrete criado!", description: `Você será notificado em ${format(remindAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}` });
      setForm({ title: "", message: "", date: "", time: "", icon: "🔔", priority: "normal" });
      setShowForm(false);
      fetchReminders();
    } catch (err: any) {
      toast({ title: "Erro ao criar lembrete", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("custom_reminders").delete().eq("id", id);
      if (error) throw error;
      setReminders((prev) => prev.filter((r) => r.id !== id));
      toast({ title: "Lembrete removido" });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  };

  const getTimeLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isPast(date)) return "Já passou";
    if (isToday(date)) return `Hoje às ${format(date, "HH:mm")}`;
    if (isTomorrow(date)) return `Amanhã às ${format(date, "HH:mm")}`;
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  };

  const pendingReminders = reminders.filter((r) => !r.sent && !isPast(new Date(r.remind_at)));
  const pastReminders = reminders.filter((r) => r.sent || isPast(new Date(r.remind_at)));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Meus Lembretes</h2>
            <p className="text-sm text-muted-foreground">Notificações push personalizadas</p>
          </div>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20"
        >
          <Plus className="w-4 h-4" />
          Novo Lembrete
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl border border-border/50 bg-card"
        >
          <div className="text-2xl font-bold text-foreground">{reminders.length}</div>
          <div className="text-xs text-muted-foreground">Total</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5"
        >
          <div className="text-2xl font-bold text-blue-600">{pendingReminders.length}</div>
          <div className="text-xs text-blue-600/70">Pendentes</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5"
        >
          <div className="text-2xl font-bold text-emerald-600">{pastReminders.filter(r => r.sent).length}</div>
          <div className="text-xs text-emerald-600/70">Enviados</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5"
        >
          <div className="text-2xl font-bold text-amber-600">
            {reminders.filter(r => r.priority === "urgent" || r.priority === "high").length}
          </div>
          <div className="text-xs text-amber-600/70">Urgentes</div>
        </motion.div>
      </div>

      {/* Pending Reminders */}
      {pendingReminders.length > 0 && (
        <Card className="border-primary/20 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="w-4 h-4 text-primary" />
              Próximos Lembretes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AnimatePresence>
              {pendingReminders.map((reminder, i) => {
                const priorityInfo = PRIORITIES.find((p) => p.value === reminder.priority);
                return (
                  <motion.div
                    key={reminder.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center gap-4 p-4 border-b border-border/30 last:border-b-0 hover:bg-muted/30 transition-colors group"
                  >
                    <div className="text-2xl flex-shrink-0">{reminder.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-foreground truncate">{reminder.title}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${priorityInfo?.color}`}>
                          {priorityInfo?.label}
                        </Badge>
                      </div>
                      {reminder.message && (
                        <p className="text-xs text-muted-foreground truncate">{reminder.message}</p>
                      )}
                      <div className="flex items-center gap-1.5 mt-1">
                        <Clock className="w-3 h-3 text-primary" />
                        <span className="text-xs text-primary font-medium">{getTimeLabel(reminder.remind_at)}</span>
                        <span className="text-xs text-muted-foreground">
                          • {format(new Date(reminder.remind_at), "dd/MM 'às' HH:mm")}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(reminder.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </CardContent>
        </Card>
      )}

      {/* Sent Reminders */}
      {pastReminders.length > 0 && (
        <Card className="border-border/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-muted-foreground">
              <CheckCircle2 className="w-4 h-4" />
              Enviados / Passados
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pastReminders.slice(0, 5).map((reminder) => (
              <div
                key={reminder.id}
                className="flex items-center gap-3 p-3 border-b border-border/20 last:border-b-0 opacity-60"
              >
                <span className="text-lg">{reminder.icon}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-foreground line-through">{reminder.title}</span>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(reminder.remind_at), "dd/MM/yyyy 'às' HH:mm")}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(reminder.id)} className="h-7 w-7">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {reminders.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
        >
          <div className="inline-flex p-4 rounded-full bg-primary/10 mb-4">
            <Bell className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Nenhum lembrete ainda</h3>
          <p className="text-sm text-muted-foreground mb-4">Crie seu primeiro lembrete e receba uma notificação push na data e hora marcada!</p>
          <Button onClick={() => setShowForm(true)} className="gap-2">
            <Plus className="w-4 h-4" /> Criar Lembrete
          </Button>
        </motion.div>
      )}

      {/* Create Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md border-primary/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Bell className="w-4 h-4 text-primary" />
              </div>
              Novo Lembrete Push
            </DialogTitle>
            <DialogDescription>
              Cadastre um lembrete e receba uma notificação push na tela do celular
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Ícone */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Ícone</Label>
              <div className="flex flex-wrap gap-1.5">
                {ICONS.map((icon) => (
                  <button
                    key={icon.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, icon: icon.value }))}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                      form.icon === icon.value
                        ? "bg-primary/20 ring-2 ring-primary scale-110"
                        : "bg-muted/50 hover:bg-muted"
                    }`}
                  >
                    {icon.value}
                  </button>
                ))}
              </div>
            </div>

            {/* Título */}
            <div>
              <Label htmlFor="r-title">Título *</Label>
              <Input
                id="r-title"
                placeholder="Ex: Ligar para cliente João"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            {/* Mensagem */}
            <div>
              <Label htmlFor="r-message">Mensagem (opcional)</Label>
              <Textarea
                id="r-message"
                placeholder="Detalhes adicionais..."
                rows={2}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
            </div>

            {/* Data e Hora */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="r-date">Data *</Label>
                <Input
                  id="r-date"
                  type="date"
                  value={form.date}
                  min={format(new Date(), "yyyy-MM-dd")}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="r-time">Hora *</Label>
                <Input
                  id="r-time"
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                />
              </div>
            </div>

            {/* Prioridade */}
            <div>
              <Label>Prioridade</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, priority: p.value }))}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                      form.priority === p.value ? `${p.color} ring-1 ring-offset-1` : "bg-muted/30 text-muted-foreground border-border/50 hover:bg-muted/50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="w-full gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {saving ? "Salvando..." : "Criar Lembrete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
